// src/context/AuthContext.jsx

import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

/**
 * 🎯 Contexto de Autenticación
 * Proporciona el estado de autenticación a toda la aplicación
 */

const AuthContext = createContext(null);

/**
 * Hook personalizado para usar el contexto de autenticación
 * Esto hace que sea más fácil acceder al contexto desde cualquier componente
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

/**
 * Provider del contexto de autenticación
 * Envuelve la aplicación y proporciona el estado y funciones de auth
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => authService.getStoredUser());
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(() =>
    authService.isAuthenticated()
  );

  // 🔄 Verificar autenticación al cargar la app
  useEffect(() => {
    const verifyAuth = async () => {
      try {
        // Si hay token, verificar que sea válido
        if (authService.isAuthenticated()) {
          const userData = await authService.getCurrentUser();
          setUser(userData);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error('Error verificando autenticación:', error);
        // Si falla la verificación, limpiamos todo
        logout();
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, []);

  /**
   * 🔐 Función de login
   */
  const login = async (email, password) => {
    try {
      const data = await authService.login(email, password);
      setUser(data.user);
      setIsAuthenticated(true);
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  /**
   * 📝 Función de registro
   */
  const register = async (userData) => {
    try {
      const data = await authService.register(userData);
      setUser(data.user);
      setIsAuthenticated(true);
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  /**
   * 🚪 Función de logout
   */
  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  };

  /**
   * 🔄 Actualizar datos del usuario
   */
  const updateUser = (userData) => {
    setUser(userData);
    const { user: userStorageKey } = authService.getStorageKeys();
    localStorage.setItem(userStorageKey, JSON.stringify(userData));
  };

  const setSession = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  // Valor que se proporciona a todos los componentes hijos
  const value = {
    user,
    token: authService.getToken(),
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    updateUser,
    setSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
